import { Cuisine50 } from 'models/cuisine_50'
import { SyndigoProduct } from 'services/products_api'

export interface Product {
  avg_serving_size_per_lb: number
  company?: number
  current_aided_awareness: number
  current_national_trial_perc: number
  est_monthly_churn_rate: number
  estimated_distr_margin_before_pos?: number
  estimated_sales_potential: {
    default_sales?: number
    cuisine_sales?: Record<Cuisine50, number>
    is_not_recurring?: boolean // Deprecated: use top-level is_not_recurring
  }
  // Non-recurring revenue flag: products that are one-time purchases (e.g., equipment)
  // and don't generate monthly recurring revenue. Affects LTV calculations.
  is_not_recurring?: boolean
  format: string
  product_format: string
  id: number
  lbs_per_case: number
  mfg_avg_list_price_per_lb: number
  name: string
  price_per_case: number
  product_target_margin_per_lb?: number
  value_per_consumer_new_informed: number
  value_per_consumer_new_trial: number
  unit?: string
  variants?: Variant[] // Optional - only included by legacy /products/ endpoint
  variant_count?: number
  variant_names?: string[] // Array of matched variant names (first 5)
  matched_variant_count?: number
  unmatched_variant_count?: number
  created?: string
  modified?: string
}

export interface DistributorCode {
  id?: number
  distributor_name: string
  distributor_code: string
}

export interface Variant {
  id?: number
  name: string
  description?: string
  product?: number | null
  gtin?: string
  sku?: string
  units_per_case?: number
  case_weight?: number
  case_weight_unit?: string
  unit_weight?: number
  unit_weight_unit?: string
  format_pack_size?: string
  avg_price?: number | null
  distributor_codes?: DistributorCode[]
  syndigo_product?: SyndigoProduct | null
}

/**
 * Checks if a product is marked as non-recurring revenue.
 * Non-recurring products are one-time purchases (e.g., seasonal items, equipment)
 * that don't generate monthly recurring revenue.
 *
 * @param product - Product object or estimated_sales_potential object
 * @returns true if the product is non-recurring, false otherwise
 */
export function isNonRecurringProduct(
  product:
    | Pick<Product, 'is_not_recurring' | 'estimated_sales_potential'>
    | Pick<Product, 'is_not_recurring'>
    | Product['estimated_sales_potential']
    | null
    | undefined
): boolean {
  if (!product) return false

  // Check top-level is_not_recurring first (new schema)
  if ('is_not_recurring' in product && product.is_not_recurring !== undefined) {
    return product.is_not_recurring === true
  }

  // Fallback to estimated_sales_potential.is_not_recurring for backwards compatibility
  if ('estimated_sales_potential' in product) {
    return product.estimated_sales_potential?.is_not_recurring === true
  }

  // Handle estimated_sales_potential object directly
  if ('default_sales' in product || 'cuisine_sales' in product) {
    return (
      (product as Product['estimated_sales_potential'])?.is_not_recurring ===
      true
    )
  }

  return false
}
